import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import it from "./locales/it";
import en from "./locales/en";
import es from "./locales/es";

// Per aggiungere una lingua: crea locales/<codice>.js e locales/legal/<codice>.js e registrala qui
const DICTIONARIES = { it, en, es };
export const SUPPORTED_LANGUAGES = Object.keys(DICTIONARIES);
const DEFAULT_LANGUAGE = "it";
// Lingua usata per i visitatori il cui browser non è in nessuna delle lingue supportate
const FALLBACK_LANGUAGE = "en";

// Prima lingua supportata tra quelle preferite del browser/dispositivo, altrimenti fallback
const detectLanguage = () => {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const base = (tag || "").toLowerCase().split("-")[0];
    if (SUPPORTED_LANGUAGES.includes(base)) return base;
  }
  return FALLBACK_LANGUAGE;
};

const lookup = (dict, key) =>
  key.split(".").reduce((node, part) => (node == null ? undefined : node[part]), dict);

const interpolate = (value, vars) =>
  vars ? value.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? vars[name] : match)) : value;

// Trasforma "testo <b>grassetto</b> testo" in nodi React usando i renderer passati in `tags`
const renderRich = (value, tags) =>
  value.split(/(<(\w+)>.*?<\/\2>)/s).reduce((nodes, part, index, parts) => {
    // split con gruppi: [testo, match completo, nome tag, testo, ...]
    if (index % 3 === 2) return nodes;
    const match = index % 3 === 1 && part.match(/^<(\w+)>(.*)<\/\1>$/s);
    if (match && tags[match[1]]) {
      nodes.push(<React.Fragment key={index}>{tags[match[1]](match[2])}</React.Fragment>);
    } else if (part) {
      nodes.push(part);
    }
    return nodes;
  }, []);

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(detectLanguage);

  // Se l'utente cambia lingua del browser a pagina aperta
  useEffect(() => {
    const onLanguageChange = () => setLang(detectLanguage());
    window.addEventListener("languagechange", onLanguageChange);
    return () => window.removeEventListener("languagechange", onLanguageChange);
  }, []);

  useEffect(() => {
    const dict = DICTIONARIES[lang];
    document.documentElement.lang = lang;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute("content", dict.meta.description);
  }, [lang]);

  const value = useMemo(() => {
    const t = (key, vars) => {
      const value = lookup(DICTIONARIES[lang], key) ?? lookup(DICTIONARIES[DEFAULT_LANGUAGE], key);
      if (value === undefined) return key;
      return typeof value === "string" ? interpolate(value, vars) : value;
    };
    t.rich = (key, tags) => renderRich(t(key), tags);
    return { lang, t };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => useContext(LanguageContext);
